/**
 * Crew tournaments, as a screen.
 *
 * The bracket is the part people look at and the smallest part of the work.
 * What this screen actually has to do is make the disagreement path obvious
 * and calm: reporting a result is one press, confirming somebody else's is
 * one press, and disputing is a deliberate second choice that asks what
 * happened instead - never a red button sitting next to a green one waiting
 * to be hit by accident.
 *
 * Three states have their own colour and their own words, because "what is
 * happening with this match" is the only question anybody asks here:
 *
 *   waiting   - nobody has reported. Either side can.
 *   reported  - one side said something; the other has a day to agree or not.
 *   disputed  - they disagree. Nothing advances until an organiser rules.
 *
 * The rules themselves live on the server (tournaments.js); this file asks
 * what it may do rather than working it out, so the two can never disagree
 * about who is allowed to press what.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, serverUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { ago } from '@/lib/notifications';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { absolute } from './user';
import { Button, Field, Input, Modal, Textarea, useConfirm, useToast } from './ui';
import { Select } from './Select';
import type { Crew } from './Crew';
import type { PublicUser } from '@/lib/types';

type TeamRef = { id: string; name: string } | null;
type MatchState = 'pending' | 'reported' | 'confirmed' | 'disputed' | 'ruled';

type Match = {
  id: string;
  round: number;
  slot: number;
  a: TeamRef;
  b: TeamRef;
  state: MatchState;
  how: string | null;
  winner: TeamRef;
  score: string | null;
  reportedBy: string | null;
  reportedAt: number | null;
  confirmBy: number | null;
  disputedBy: string | null;
  disputeNote: string | null;
  evidence: { url: string; by: string; at: number }[];
  ruledBy: string | null;
  ruledNote: string | null;
  playedAt: number | null;
  mine: boolean;
};

type Team = { id: string; name: string; captainId: string; at: number; players: PublicUser[] };

export type Tournament = {
  id: string;
  crewId: string;
  title: string;
  gameId: string | null;
  format: 'bo1' | 'bo3' | 'bo5';
  state: 'draft' | 'open' | 'running' | 'done' | 'cancelled';
  createdAt: number;
  startsAt: number | null;
  confirmHours: number;
  canOrganise: boolean;
  myTeamId: string | null;
  winner: TeamRef;
  teams: Team[];
  matches: Match[];
  rounds: number;
  log: { at: number; what: string; detail: unknown; by: PublicUser | null }[];
};

/* ------------------------------------------------------------ the card */

export function Tournaments({ crew, canManage }: { crew: Crew; canManage: boolean }) {
  const [making, setMaking] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['tournaments', crew.id],
    queryFn: () => api.get<{ tournaments: Tournament[] }>('/api/crews/' + crew.id + '/tournaments'),
    enabled: !!crew.myRole,
    staleTime: 30_000
  });

  if (!crew.myRole) return null;
  const list = q.data?.tournaments ?? [];
  const live = list.filter((t) => t.state === 'open' || t.state === 'running');
  const past = list.filter((t) => t.state === 'done');
  const shown = open ? list.find((t) => t.id === open) : null;

  if (!live.length && !past.length && !canManage) return null;

  return (
    <section className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="mb-3 flex items-center gap-2.5">
        <Icon name="trophy" size={18} className="text-ink" />
        <h3 className="m-0 flex-1 text-[16px] font-bold text-ink">Tournaments</h3>
        {canManage ? <Button size="sm" kind="outline" onClick={() => setMaking(true)}>Run one</Button> : null}
      </div>

      {!live.length && !past.length ? (
        <p className="m-0 text-[13.5px] leading-relaxed text-mute">
          A weekend bracket for the crew. Teams enter, matches are played whenever both sides can,
          and a result only counts once both sides agree - or an organiser settles it.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {[...live, ...past].map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => setOpen(t.id)}
                className="flex w-full items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3 text-left outline-none transition-colors hover:bg-surface-3"
              >
                {t.gameId ? <GameMark id={t.gameId} size={30} plain /> : <Icon name="trophy" size={22} className="flex-none text-accent" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-bold text-ink">{t.title}</span>
                  <span className="block truncate text-[12px] text-mute">
                    {t.state === 'open' ? t.teams.length + (t.teams.length === 1 ? ' team entered · entries open' : ' teams entered · entries open')
                      : t.state === 'running' ? liveLine(t)
                        : 'Won by ' + (t.winner?.name ?? 'nobody')}
                  </span>
                </span>
                {disputesIn(t) ? (
                  <span className="flex flex-none items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--bad)_18%,transparent)] px-2 py-0.5 text-[11.5px] font-bold text-bad">
                    <Icon name="alert" size={11} />
                    {disputesIn(t)}
                  </span>
                ) : null}
                <Icon name="chevronR" size={15} className="flex-none text-ghost" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {making ? <NewTournament crew={crew} onClose={() => setMaking(false)} /> : null}
      {shown ? <TournamentPanel crew={crew} t={shown} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

function liveLine(t: Tournament): string {
  const left = t.matches.filter((m) => m.a && m.b && m.state !== 'confirmed' && m.state !== 'ruled').length;
  return left ? left + (left === 1 ? ' match to play' : ' matches to play') : 'Finishing up';
}

function disputesIn(t: Tournament): number {
  return t.matches.filter((m) => m.state === 'disputed').length;
}

/* --------------------------------------------------------- making one */

function NewTournament({ crew, onClose }: { crew: Crew; onClose: () => void }) {
  const catalog = useSession((s) => s.catalog);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [gameId, setGameId] = useState('');
  const [format, setFormat] = useState('bo1');
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      await api.post('/api/crews/' + crew.id + '/tournaments', { title, gameId: gameId || null, format });
      void queryClient.invalidateQueries({ queryKey: ['tournaments', crew.id] });
      toast('Entries are open', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Run a tournament"
      subtitle="Teams enter, you draw the bracket when everybody is in."
      onClose={onClose}
      footer={
        <>
          <span className="flex-1" />
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={busy || title.trim().length < 3} onClick={() => void create()}>
            {busy ? 'Opening…' : 'Open entries'}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Saturday bracket" maxLength={48} />
      </Field>
      <Field label="Game" hint="Optional - it only decides which mark is drawn beside it.">
        <Select
          value={gameId}
          onChange={setGameId}
          options={[{ value: '', label: 'Any game' }].concat((catalog?.games ?? []).map((g) => ({ value: g.id, label: g.name })))}
        />
      </Field>
      <Field label="Format">
        <Select
          value={format}
          onChange={setFormat}
          options={[
            { value: 'bo1', label: 'One game' },
            { value: 'bo3', label: 'Best of three' },
            { value: 'bo5', label: 'Best of five' }
          ]}
        />
      </Field>
      <p className="mt-1 mb-0 text-[12.5px] leading-relaxed text-mute">
        Single elimination, seeded in the order teams enter. Either side reports a result, the other
        confirms it, and anything nobody objects to within a day stands by itself.
      </p>
    </Modal>
  );
}

/* ------------------------------------------------------------ the panel */

function TournamentPanel({ crew, t, onClose }: { crew: Crew; t: Tournament; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [entering, setEntering] = useState(false);
  const [report, setReport] = useState<Match | null>(null);
  const [dispute, setDispute] = useState<Match | null>(null);
  const [rule, setRule] = useState<Match | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tournaments', crew.id] });

  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api.post(path, body ?? {}),
    onSuccess: () => refresh(),
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const rounds: Match[][] = [];
  for (let r = 1; r <= t.rounds; r++) rounds.push(t.matches.filter((m) => m.round === r));

  async function start() {
    const ok = await confirm(
      'Start ' + t.title + '?',
      'The bracket is drawn from the ' + t.teams.length + ' teams entered, and nobody else can join after this.',
      { confirmLabel: 'Draw the bracket' }
    );
    if (ok) act.mutate({ path: '/api/tournaments/' + t.id + '/start' });
  }

  return (
    <Modal title={t.title} subtitle={subtitleFor(t)} onClose={onClose} xl footer={
      <>
        <span className="flex-1 text-[11.5px] text-ghost">
          A result stands once both sides agree, or after {t.confirmHours} hours with nobody objecting.
        </span>
        {t.state === 'open' && t.canOrganise ? (
          <Button kind="primary" disabled={t.teams.length < 2} onClick={() => void start()}>Start it</Button>
        ) : null}
        {t.state === 'open' && !t.myTeamId ? (
          <Button kind="primary" onClick={() => setEntering(true)}>Enter a team</Button>
        ) : null}
      </>
    }>
      {dialog}

      {t.state === 'open' ? (
        <Teams crew={crew} t={t} onLeave={(teamId) => act.mutate({ path: '/api/tournaments/' + t.id + '/teams/' + teamId })} />
      ) : (
        <>
          {/* Anything that needs somebody, first. A bracket where the one
              match waiting on you is three columns to the right is a bracket
              nobody finishes. */}
          <NeedsYou
            t={t}
            onReport={setReport}
            onConfirm={(m) => act.mutate({ path: '/api/tournaments/' + t.id + '/matches/' + m.id + '/confirm' })}
            onDispute={setDispute}
            onRule={setRule}
          />

          <div className="mt-4 flex gap-4 overflow-x-auto pb-2">
            {rounds.map((list, i) => (
              <div key={i} className="flex min-w-[240px] flex-1 flex-col gap-2">
                <h4 className="m-0 text-[12px] font-semibold tracking-[.06em] text-ghost uppercase">
                  {roundName(i + 1, t.rounds)}
                </h4>
                {list.map((m) => <MatchRow key={m.id} m={m} t={t} onOpen={() => (m.mine && m.state !== 'confirmed' && m.state !== 'ruled' ? setReport(m) : t.canOrganise && m.state === 'disputed' ? setRule(m) : undefined)} />)}
              </div>
            ))}
          </div>

          {t.winner ? (
            <p className="mt-4 mb-0 rounded-[calc(14px*var(--radius-mul,1))] bg-accent-soft p-3 text-center text-[14px] font-bold text-accent-hi">
              {t.winner.name} won it.
            </p>
          ) : null}

          <History t={t} />
        </>
      )}

      {entering ? <EnterTeam crew={crew} t={t} onClose={() => setEntering(false)} /> : null}
      {report ? <ReportResult t={t} m={report} onClose={() => setReport(null)} onDispute={() => { setDispute(report); setReport(null); }} /> : null}
      {dispute ? <RaiseDispute t={t} m={dispute} onClose={() => setDispute(null)} /> : null}
      {rule ? <RuleOn t={t} m={rule} onClose={() => setRule(null)} /> : null}
    </Modal>
  );
}

function subtitleFor(t: Tournament): string {
  if (t.state === 'open') return 'Entries are open · ' + t.teams.length + (t.teams.length === 1 ? ' team' : ' teams');
  if (t.state === 'running') return liveLine(t);
  return 'Finished ' + ago(t.matches.reduce((a, m) => Math.max(a, m.playedAt ?? 0), 0));
}

function roundName(round: number, total: number): string {
  const from = total - round;
  if (from === 0) return 'Final';
  if (from === 1) return 'Semi-finals';
  if (from === 2) return 'Quarter-finals';
  return 'Round ' + round;
}

/* ---------------------------------------------------------- the teams */

function Teams({ crew, t, onLeave }: { crew: Crew; t: Tournament; onLeave: (teamId: string) => void }) {
  return (
    <>
      <p className="mt-0 mb-3 text-[13.5px] text-mute">
        Anybody in {crew.name} can enter a team. Nobody plays in two.
      </p>
      {t.teams.length ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {t.teams.map((team, i) => (
            <li key={team.id} className="flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3">
              <span className="grid h-8 w-8 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-surface-3 text-[12.5px] font-bold text-dim tabular-nums">
                {i + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-bold text-ink">{team.name}</span>
                <span className="block truncate text-[12px] text-mute">
                  {team.players.map((p) => p.displayName).join(', ')}
                </span>
              </span>
              <span className="flex flex-none items-center">
                {team.players.slice(0, 4).map((p, j) => (
                  <span key={p.id} className="rounded-full ring-2 ring-[var(--bg-2)]" style={{ marginLeft: j ? -8 : 0 }}>
                    <Avatar user={p} size={24} />
                  </span>
                ))}
              </span>
              {t.myTeamId === team.id || t.canOrganise ? (
                <Button size="sm" kind="ghost" onClick={() => onLeave(team.id)}>
                  {t.myTeamId === team.id ? 'Withdraw' : 'Remove'}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-4 text-center text-[13.5px] text-mute">
          No teams yet. The first one in is seeded first.
        </p>
      )}
    </>
  );
}

function EnterTeam({ crew, t, onClose }: { crew: Crew; t: Tournament; onClose: () => void }) {
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const taken = new Set(t.teams.flatMap((team) => team.players.map((p) => p.id)));
  const free = crew.members.filter((m) => m.user.id !== me?.id && !taken.has(m.user.id));

  async function enter() {
    setBusy(true);
    try {
      await api.post('/api/tournaments/' + t.id + '/teams', { name, players: picked });
      void queryClient.invalidateQueries({ queryKey: ['tournaments', crew.id] });
      toast('You are in', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Enter a team"
      subtitle="You are the captain. Pick who is playing with you."
      onClose={onClose}
      footer={
        <>
          <span className="flex-1" />
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={busy || name.trim().length < 2} onClick={() => void enter()}>
            {busy ? 'Entering…' : 'Enter'}
          </Button>
        </>
      }
    >
      <Field label="Team name">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={32} placeholder="The Late Shift" />
      </Field>
      <Field label="Who is playing" hint="Everybody has to be in the crew and not already in a team.">
        {free.length ? (
          <ul className="m-0 flex max-h-[240px] list-none flex-col gap-1 overflow-y-auto p-0">
            {free.map((m) => {
              const on = picked.includes(m.user.id);
              return (
                <li key={m.user.id}>
                  <button
                    type="button"
                    onClick={() => setPicked((list) => (on ? list.filter((x) => x !== m.user.id) : [...list, m.user.id]))}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] px-2.5 py-2 text-left outline-none transition-colors',
                      on ? 'bg-accent-soft' : 'bg-surface-2 hover:bg-surface-3'
                    )}
                  >
                    <Avatar user={m.user} size={26} />
                    <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-ink">{m.user.displayName}</span>
                    {on ? <Icon name="check" size={15} className="flex-none text-accent" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="m-0 text-[13px] text-mute">Everybody else is already in a team. You can enter on your own.</p>
        )}
      </Field>
    </Modal>
  );
}

/* --------------------------------------------------------- the matches */

/** The matches waiting on this person, pulled to the top. */
function NeedsYou({ t, onReport, onConfirm, onDispute, onRule }: {
  t: Tournament;
  onReport: (m: Match) => void;
  onConfirm: (m: Match) => void;
  onDispute: (m: Match) => void;
  onRule: (m: Match) => void;
}) {
  const me = useSession((s) => s.me);
  const mine = t.matches.filter((m) => m.mine && m.a && m.b && m.state !== 'confirmed' && m.state !== 'ruled');
  const toRule = t.canOrganise ? t.matches.filter((m) => m.state === 'disputed') : [];
  if (!mine.length && !toRule.length) return null;

  return (
    <div className="flex flex-col gap-2">
      {toRule.map((m) => (
        <div key={m.id} className="flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-[color-mix(in_srgb,var(--bad)_12%,var(--bg-2))] p-3 shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--bad)_28%,transparent)]">
          <Icon name="alert" size={18} className="flex-none text-bad" />
          <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-bold text-ink">{m.a?.name} v {m.b?.name}</span>
            <span className="block truncate text-[12px] text-dim">{m.disputeNote}</span>
          </span>
          <Button size="sm" kind="primary" onClick={() => onRule(m)}>Rule on it</Button>
        </div>
      ))}

      {mine.map((m) => {
        const iReported = m.state === 'reported' && m.reportedBy === me?.id;
        return (
          <div key={m.id} className="flex flex-wrap items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3">
            <span className="min-w-0 flex-1">
              <span className="block text-[13.5px] font-bold text-ink">{m.a?.name} v {m.b?.name}</span>
              <span className="block truncate text-[12px] text-mute">
                {m.state === 'pending' ? 'Nobody has reported this yet'
                  : m.state === 'disputed' ? 'Disputed - an organiser will settle it'
                    : iReported ? 'You reported ' + (m.winner?.name ?? '') + ' - waiting on the other side'
                      : 'They say ' + (m.winner?.name ?? '') + ' won' + (m.score ? ' (' + m.score + ')' : '')}
              </span>
            </span>
            {m.state === 'pending' ? (
              <Button size="sm" kind="primary" onClick={() => onReport(m)}>Report the result</Button>
            ) : m.state === 'reported' && !iReported ? (
              <>
                <Button size="sm" kind="ghost" onClick={() => onDispute(m)}>That is not right</Button>
                <Button size="sm" kind="primary" onClick={() => onConfirm(m)}>Confirm</Button>
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function MatchRow({ m, t, onOpen }: { m: Match; t: Tournament; onOpen: () => void }) {
  const settled = m.state === 'confirmed' || m.state === 'ruled';
  const empty = !m.a && !m.b;
  if (empty && settled) return null;

  const side = (team: TeamRef) => (
    <span className={cn('block truncate text-[13px]', team && m.winner?.id === team.id ? 'font-bold text-ink' : settled ? 'text-mute' : 'text-dim')}>
      {team?.name ?? <span className="text-ghost">To be decided</span>}
    </span>
  );

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex w-full flex-col gap-1 rounded-[calc(12px*var(--radius-mul,1))] p-2.5 text-left outline-none transition-colors',
        m.state === 'disputed'
          ? 'bg-[color-mix(in_srgb,var(--bad)_12%,var(--bg-2))] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--bad)_30%,transparent)]'
          : m.mine && !settled ? 'bg-surface-2 shadow-[inset_0_0_0_1px_var(--accent-line)] hover:bg-surface-3'
            : 'bg-surface-2 hover:bg-surface-3'
      )}
    >
      {side(m.a)}
      {side(m.b)}
      <span className="mt-0.5 flex items-center gap-1.5 text-[11px]">
        {m.how === 'bye' ? <span className="text-ghost">Bye</span>
          : m.state === 'disputed' ? <span className="font-semibold text-bad">Disputed</span>
            : m.state === 'reported' ? <span className="font-semibold text-warn">Waiting on the other side</span>
              : settled ? <span className="text-ghost">{m.score ? m.score + ' · ' : ''}{m.how === 'ruled' ? 'Ruled' : m.how === 'timeout' ? 'Stood unchallenged' : 'Agreed'}</span>
                : <span className="text-ghost">{t.state === 'running' && m.a && m.b ? 'Not played yet' : 'Waiting'}</span>}
      </span>
    </button>
  );
}

/* ------------------------------------------------------- the dialogues */

function ReportResult({ t, m, onClose, onDispute }: { t: Tournament; m: Match; onClose: () => void; onDispute: () => void }) {
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [winner, setWinner] = useState(m.winner?.id ?? '');
  const [score, setScore] = useState(m.score ?? '');
  const [busy, setBusy] = useState(false);
  const theirs = m.state === 'reported' && m.reportedBy !== me?.id;

  async function send() {
    setBusy(true);
    try {
      await api.post('/api/tournaments/' + t.id + '/matches/' + m.id + '/report', { winner, score });
      void queryClient.invalidateQueries({ queryKey: ['tournaments', t.crewId] });
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={theirs ? 'They reported a result' : 'Report the result'}
      subtitle={m.a?.name + ' v ' + m.b?.name}
      onClose={onClose}
      footer={
        <>
          {theirs ? <Button kind="ghost" onClick={onDispute}>That is not what happened</Button> : null}
          <span className="flex-1" />
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={busy || !winner} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Report it'}
          </Button>
        </>
      }
    >
      <Field label="Who won">
        <div className="grid gap-2 sm:grid-cols-2">
          {[m.a, m.b].map((team) => team ? (
            <button
              key={team.id}
              type="button"
              onClick={() => setWinner(team.id)}
              className={cn(
                'rounded-[calc(12px*var(--radius-mul,1))] px-3 py-3 text-[14px] font-bold outline-none transition-colors',
                winner === team.id ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
              )}
            >
              {team.name}
            </button>
          ) : null)}
        </div>
      </Field>
      <Field label="Score" hint="Optional, and only for the record.">
        <Input value={score} onChange={(e) => setScore(e.target.value)} placeholder="13-9" maxLength={12} />
      </Field>
      <p className="mt-1 mb-0 text-[12.5px] leading-relaxed text-mute">
        The other side gets {t.confirmHours} hours to confirm or dispute it. If nobody says anything, it stands.
      </p>
    </Modal>
  );
}

function RaiseDispute({ t, m, onClose }: { t: Tournament; m: Match; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [shots, setShots] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  async function upload(file: File) {
    try {
      const res = await fetch(serverUrl() + '/api/files', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + (localStorage.getItem('pogu.token') ?? ''),
          'Content-Type': file.type || 'application/octet-stream',
          'X-File-Type': file.type || 'application/octet-stream',
          'X-File-Name': encodeURIComponent(file.name),
          'X-File-Kind': 'attachment'
        },
        body: file
      });
      if (!res.ok) throw new Error('That screenshot would not upload.');
      const saved = await res.json();
      setShots((list) => [...list, saved.file?.id ?? saved.id].slice(0, 4));
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function send() {
    setBusy(true);
    try {
      await api.post('/api/tournaments/' + t.id + '/matches/' + m.id + '/dispute', { note, evidence: shots });
      void queryClient.invalidateQueries({ queryKey: ['tournaments', t.crewId] });
      toast('An organiser will look at it', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Dispute the result"
      subtitle={m.a?.name + ' v ' + m.b?.name}
      onClose={onClose}
      footer={
        <>
          <span className="flex-1" />
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={busy || note.trim().length < 4} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send it to an organiser'}
          </Button>
        </>
      }
    >
      <p className="mt-0 mb-3 text-[13px] leading-relaxed text-dim">
        The bracket stops here until an organiser rules. Say what happened instead - a screenshot helps, but
        it is not required, because the side without one still has to be able to object.
      </p>
      <Field label="What happened">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={400} placeholder="We won 13-11 - they left before the last round." />
      </Field>
      <Field label="Screenshots" hint="Up to four. Only the two teams and the organisers see them.">
        <div className="flex flex-wrap items-center gap-2">
          {shots.map((id) => (
            <span key={id} className="h-[52px] w-[78px] overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] bg-surface-3">
              <img src={absolute('/api/files/' + id)} alt="" className="h-full w-full object-cover" />
            </span>
          ))}
          <label className="flex h-[52px] cursor-pointer items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 px-3 text-[12.5px] font-semibold text-mute transition-colors hover:bg-surface-3 hover:text-ink">
            <Icon name="image" size={14} />
            Add
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
          </label>
        </div>
      </Field>
    </Modal>
  );
}

function RuleOn({ t, m, onClose }: { t: Tournament; m: Match; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [winner, setWinner] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function send() {
    setBusy(true);
    try {
      await api.post('/api/tournaments/' + t.id + '/matches/' + m.id + '/rule', { winner, note });
      void queryClient.invalidateQueries({ queryKey: ['tournaments', t.crewId] });
      toast('Decided - the bracket has moved on', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Rule on this match"
      subtitle={m.a?.name + ' v ' + m.b?.name}
      onClose={onClose}
      footer={
        <>
          <span className="flex-1" />
          <Button kind="ghost" onClick={onClose}>Not yet</Button>
          <Button kind="primary" disabled={busy || !winner} onClick={() => void send()}>
            {busy ? 'Deciding…' : 'Decide it'}
          </Button>
        </>
      }
    >
      <div className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3">
        <p className="m-0 text-[12px] font-semibold tracking-[.05em] text-ghost uppercase">Reported</p>
        <p className="mt-1 mb-2 text-[13.5px] text-ink">
          {m.winner?.name ?? 'Nobody'} won{m.score ? ', ' + m.score : ''} · reported {m.reportedAt ? ago(m.reportedAt) : ''}
        </p>
        <p className="m-0 text-[12px] font-semibold tracking-[.05em] text-ghost uppercase">Disputed</p>
        <p className="mt-1 mb-0 text-[13.5px] whitespace-pre-wrap text-dim">{m.disputeNote}</p>
        {m.evidence.length ? (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {m.evidence.map((e) => (
              <a key={e.url} href={absolute(e.url)} target="_blank" rel="noreferrer" className="h-[58px] w-[86px] overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] bg-surface-3">
                <img src={absolute(e.url)} alt="" className="h-full w-full object-cover" />
              </a>
            ))}
          </div>
        ) : null}
      </div>

      <Field label="Who goes through">
        <div className="grid gap-2 sm:grid-cols-2">
          {[m.a, m.b].map((team) => team ? (
            <button
              key={team.id}
              type="button"
              onClick={() => setWinner(team.id)}
              className={cn(
                'rounded-[calc(12px*var(--radius-mul,1))] px-3 py-3 text-[14px] font-bold outline-none transition-colors',
                winner === team.id ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
              )}
            >
              {team.name}
            </button>
          ) : null)}
        </div>
      </Field>
      <Field label="Why" hint="Both teams see this. A ruling nobody can read is a ruling they will argue with again.">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={400} />
      </Field>
    </Modal>
  );
}

/** Everything that has happened, for the people who want to check. */
function History({ t }: { t: Tournament }) {
  const [open, setOpen] = useState(false);
  if (!t.log.length) return null;
  return (
    <div className="mt-4 border-t border-line-soft pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-[12.5px] font-semibold text-mute outline-none transition-colors hover:text-ink"
      >
        <Icon name={open ? 'chevronD' : 'chevronR'} size={13} />
        What happened, in order
      </button>
      {open ? (
        <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
          {t.log.slice().reverse().map((l, i) => (
            <li key={i} className="flex items-baseline gap-2 text-[12px] text-mute">
              <span className="flex-none tabular-nums text-ghost">{ago(l.at)}</span>
              <span className="min-w-0">
                {l.by ? <b className="font-semibold text-dim">{l.by.displayName}</b> : <b className="font-semibold text-dim">Pugo</b>}{' '}
                {LOG_WORDS[l.what] ?? l.what}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const LOG_WORDS: Record<string, string> = {
  opened: 'opened entries',
  'team-entered': 'entered a team',
  'team-withdrew': 'withdrew a team',
  'player-left': 'left a team',
  started: 'drew the bracket',
  reported: 'reported a result',
  disputed: 'disputed a result',
  settled: 'settled a match',
  cancelled: 'called it off'
};
